import { Wallet, PieChart, Clock, Home, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../contexts/LayoutContext';
import { useAuth } from '../contexts/AuthContext';

interface SidePanelProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

export default function SidePanel({ currentPage, onNavigate }: SidePanelProps) {
  const { t } = useTranslation();
  const { isSidebarOpen } = useLayout();
  const { user } = useAuth();

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const navItems = [
    { id: 'home', icon: Home, label: t('nav.home') },
    { id: 'budget', icon: PieChart, label: t('nav.budget') },
    { id: 'rekening', icon: Wallet, label: t('nav.accounts') },
    { id: 'history', icon: Clock, label: t('nav.transactions') },
    { id: 'profile', icon: User, label: t('nav.profile') },
  ];

  const renderNavItem = (item: typeof navItems[0]) => {
    const Icon = item.icon;
    const isActive = currentPage === item.id;

    return (
      <button
        key={item.id}
        onClick={() => onNavigate(item.id)}
        className={`w-full flex items-center gap-4 px-4 py-3 transition-colors duration-200 group ${
          isActive 
            ? 'bg-[#a9edba] text-[#077f5d] border-r-4 border-[#077f5d]' 
            : 'text-gray-600 hover:bg-gray-50 hover:text-[#077f5d]'
        }`}
      >
        <Icon 
          className={`w-6 h-6 flex-shrink-0 ${isActive ? 'stroke-[2.5]' : 'stroke-[2]'}`} 
        />
        <span 
          className={`font-mono uppercase tracking-wide text-sm whitespace-nowrap overflow-hidden transition-all duration-300 opacity-100 w-auto ${isActive ? 'font-bold' : 'font-medium'}`}
          style={isActive ? { fontFamily: "'Press Start 2P', monospace", fontSize: '10px' } : {}}
        >
          {item.label}
        </span>
      </button>
    );
  };

  return (
    <aside
      className={`hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-white border-r-2 border-[#077f5d] transition-all duration-300 z-50 w-64 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ boxShadow: isSidebarOpen ? '2px 0 0 rgba(7, 127, 93, 0.1)' : 'none' }}
    >
      {/* Logo / Brand Area / Profile Area */}
      <div className="min-h-none bg-[#3cbe8c] border-b-4 border-[#077f5d] flex items-center justify-center transition-all duration-300 py-2">
         {user ? (
           <div className="flex items-center gap-3 px-4">
             <div 
               className="w-12 h-12 bg-white border-2 border-[#077f5d] flex items-center justify-center text-[#077f5d] text-lg font-bold flex-shrink-0"
               style={{ boxShadow: '2px 2px 0 #077f5d', fontFamily: "'IBM Plex Mono', monospace" }}
             >
               {user.email ? getInitials(user.email) : 'U'}
             </div>
             <div className="flex flex-col min-w-0 flex-1">
               <h2 
                 className="text-white font-bold text-sm truncate"
                 style={{ fontFamily: "'IBM Plex Mono', monospace" }}
               >
                 {user.user_metadata?.name || user.email?.split('@')[0] || 'User'}
               </h2>
               <p className="text-[#077f5d] text-xs font-mono truncate">
                 {user.email}
               </p>
             </div>
           </div>
         ) : (
           <h1 
             className="text-white font-bold text-xl tracking-widest uppercase"
             style={{ fontFamily: "'Press Start 2P', monospace" }}
           >
             FITTORY
           </h1>
         )}
      </div>

      <nav className="flex-1 flex flex-col py-6 gap-2 overflow-y-auto scrollbar-hide">
        {navItems.map(renderNavItem)}
      </nav>
    </aside>
  );
}
